/**
 * Public dynamic-config middleware for React Router v7 SSR
 *
 * Fetches a fixed set of public dynamic-config keys (e.g. `banner`, `carousel`)
 * on the server in parallel and exposes them via context. The root loader
 * passes them to the client through loader data, where
 * `HydratedDynamicConfigProvider` injects them into a `<script>` tag so
 * `usePublicDynamicConfig` can return the value synchronously on mount —
 * removing the first-paint network round-trip that otherwise causes the
 * banner carousel to pop in after hydration.
 *
 * The endpoint is `${VITE_API_URL}/config/{key}` (axios `dynamicConfigClient`
 * mirror, but using native fetch — `dynamicConfigClient` calls `getApiUrl()`
 * which reads localStorage and is unsafe to import on the server).
 *
 * @see src/services/restful/dynamicConfig.ts
 */
import { createContext, type MiddlewareFunction } from 'react-router'

const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.pixai.art'

/**
 * Public dynamic-config keys to preload on the server.
 * Add a key here when you want a `usePublicDynamicConfig(<key>)` consumer
 * to receive its data synchronously on first paint.
 *
 * Cost: one extra outbound HTTP request per page-load per key. Only add
 * keys that are actually read above the fold.
 */
const PRELOAD_KEYS = ['banner', 'carousel'] as const

/**
 * Per-request fetch budget — bounds the SSR delay added by config preload.
 * The middleware `await`s the parallel fetches before yielding to `next()`,
 * so each request adds up to this much latency in the worst case.
 */
const FETCH_TIMEOUT_MS = 800

export type HydratedDynamicConfig = Record<string, unknown>

/**
 * React Router context for server-fetched public dynamic configs.
 * Use `context.get(dynamicConfigContext)` in loaders.
 */
export const dynamicConfigContext = createContext<HydratedDynamicConfig>({})

// Use console.warn instead of `src/utils/logger.server` (winston): the
// route module that consumes this middleware is also reachable from the
// client bundle, and `.server.ts` stripping isn't reliable across every
// bundler config (notably the vitest browser config which omits the
// React Router plugin). A top-level winston import drags `colors` /
// `has-flag` into the client and trips on `process is not defined`.
//
// Same reasoning for Sentry: don't import `ssr-caught-error.server` at
// module top-level — defer to a dynamic import on the cold failure path
// so the client bundle never pulls in `@sentry/node`. The
// `import.meta.env.SSR` guard is needed too: Vite statically analyzes
// `import(...)` and otherwise still pulls `@sentry/node` into the client.
function reportToSentry(
  err: unknown,
  key: string,
  status: number | undefined,
): void {
  if (!import.meta.env.SSR) return
  void import('src/utils/ssr-caught-error.server')
    .then(({ reportSsrCaughtError }) =>
      reportSsrCaughtError(err, {
        origin: 'dynamic-config-middleware',
        operation: key,
        extras: status !== undefined ? { httpStatus: status } : undefined,
      }),
    )
    .catch(() => {
      // Observability must never throw into the request path.
    })
}

async function fetchOne(key: string): Promise<[string, unknown] | null> {
  const url = `${API_URL.replace(/\/$/, '')}/config/${key}`
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn('dynamicConfig fetch non-ok', {
        key,
        status: res.status,
      })
      reportToSentry(
        new Error(`dynamicConfig HTTP ${res.status}`),
        key,
        res.status,
      )
      return null
    }
    return [key, await res.json()]
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('dynamicConfig fetch failed', {
      key,
      error: err instanceof Error ? err.message : String(err),
    })
    reportToSentry(err, key, undefined)
    return null
  }
}

export const dynamicConfigMiddleware: MiddlewareFunction = async (
  { context },
  next,
) => {
  const results = await Promise.all(PRELOAD_KEYS.map(fetchOne))
  const map: HydratedDynamicConfig = {}
  for (const entry of results) {
    if (entry) map[entry[0]] = entry[1]
  }
  context.set(dynamicConfigContext, map)
  return next()
}
